(() => {
  const initialized = new WeakSet();
  const railControllers = new WeakMap();

  function activateTab(tablist, tab) {
    const tabs = [...tablist.querySelectorAll('[data-featured-products-tab]')];
    const root = tablist.closest('[data-featured-products]');
    if (!root) return;

    tabs.forEach((item) => {
      const active = item === tab;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
      const panel = root.querySelector(`#${item.getAttribute('aria-controls')}`);
      if (panel) {
        panel.hidden = !active;
        const rail = panel.querySelector('[data-featured-products-rail]');
        const controller = railControllers.get(rail);
        if (active) controller?.activate();
        else controller?.deactivate();
      }
    });
  }

  function initialize(tablist) {
    if (initialized.has(tablist)) return;
    initialized.add(tablist);
    const tabs = [...tablist.querySelectorAll('[data-featured-products-tab]')];

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        activateTab(tablist, tab);
        const root = tablist.closest('[data-featured-products]');
        const panel = root?.querySelector(`#${tab.getAttribute('aria-controls')}`);
        panel?.querySelector('[data-featured-products-rail]')?.scrollTo({ left: 0, behavior: 'auto' });
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let targetIndex = index;
        if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = tabs.length - 1;
        const target = tabs[targetIndex];
        activateTab(tablist, target);
        target.focus();
      });
    });

    const root = tablist.closest('[data-featured-products]');
    root?.querySelectorAll('[data-featured-products-rail]').forEach((rail) => initializeRail(rail));
  }

  function initializeRail(rail) {
    if (initialized.has(rail)) return;
    initialized.add(rail);

    const shell = rail.closest('.featured-products__rail-shell');
    const previous = shell?.querySelector('[data-featured-products-previous]');
    const next = shell?.querySelector('[data-featured-products-next]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timer;
    let resumeTimer;
    let manualPauseUntil = 0;

    const scrollAmount = () => Math.max(
      rail.querySelector('.featured-product-card')?.getBoundingClientRect().width || 0,
      rail.clientWidth * 0.7
    );

    const updateControls = () => {
      const scrollable = rail.scrollWidth > rail.clientWidth + 2;
      if (previous) previous.disabled = !scrollable || rail.scrollLeft <= 2;
      if (next) next.disabled = !scrollable || rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    };

    const stopAutoplay = () => {
      window.clearInterval(timer);
      timer = undefined;
    };

    const startAutoplay = () => {
      if (reducedMotion || timer || document.hidden || rail.scrollWidth <= rail.clientWidth + 2) return;
      if (Date.now() < manualPauseUntil) {
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(startAutoplay, manualPauseUntil - Date.now());
        return;
      }
      timer = window.setInterval(() => {
        if (document.hidden) return;
        const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
        rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + scrollAmount(), behavior: 'smooth' });
      }, 6500);
    };

    const pauseAfterManualInput = () => {
      manualPauseUntil = Date.now() + 12000;
      window.clearTimeout(resumeTimer);
      stopAutoplay();
      resumeTimer = window.setTimeout(startAutoplay, 12000);
    };

    const activate = () => {
      updateControls();
      startAutoplay();
    };

    const deactivate = () => {
      window.clearTimeout(resumeTimer);
      stopAutoplay();
    };

    const handleVisibilityChange = () => (document.hidden ? stopAutoplay() : startAutoplay());
    const handleResize = () => updateControls();
    const destroy = () => {
      deactivate();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleResize);
      initialized.delete(rail);
      railControllers.delete(rail);
    };

    railControllers.set(rail, { activate, deactivate, destroy });

    previous?.addEventListener('click', () => {
      pauseAfterManualInput();
      rail.scrollBy({ left: -scrollAmount(), behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    next?.addEventListener('click', () => {
      pauseAfterManualInput();
      rail.scrollBy({ left: scrollAmount(), behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    rail.addEventListener('scroll', updateControls, { passive: true });
    rail.addEventListener('pointerdown', pauseAfterManualInput, { passive: true });
    rail.addEventListener('wheel', pauseAfterManualInput, { passive: true });
    rail.addEventListener('pointerenter', stopAutoplay);
    rail.addEventListener('pointerleave', startAutoplay);
    rail.addEventListener('focusin', stopAutoplay);
    rail.addEventListener('focusout', (event) => {
      if (!rail.contains(event.relatedTarget)) startAutoplay();
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('resize', handleResize, { passive: true });
    activate();
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-featured-products] [role="tablist"]').forEach(initialize);
  }

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const rails = new Set(event.target.matches?.('[data-featured-products-rail]') ? [event.target] : []);
    event.target.querySelectorAll('[data-featured-products-rail]').forEach((rail) => rails.add(rail));
    rails.forEach((rail) => railControllers.get(rail)?.destroy());
  });
})();
